import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import {
  BrowserRouter as Router,
  useParams,
  Link,
  Redirect,
  Route,
  Switch,
} from "react-router-dom";
import {
  FaBookOpen,
  FaCalendarAlt,
  FaChartBar,
  FaClipboardList,
  FaCog,
  FaHandshake,
  FaIdCard,
  FaListAlt,
  FaPlus,
  FaSort,
  FaSyncAlt,
  FaTicketAlt,
  FaUserEdit,
  FaUsers,
  FaUserFriends,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import BackButton from "../ChildComponent/BackButton";
import ExamDateSheet from "./ExamDateSheet";
import ExamAdmitCard from "./ExamAdmitCard";
import ExamHallTicket from "./ExamHallTicket";
import ExamGuideline from "./ExamGuideline";
import ExamMarkingSystem from "./ExamMarkingSystem";
import ExamAddMarkStudentWise from "./ExamAddMarkStudentWise";
import ExamPaperSettings from "./ExamPaperSettings";
import ExamAddMarkSubjectWise from "./ExamAddMarkSubjectWise";
import ManageSocialValueField from "./ManageSocialValueField";
import ManageCoEducationField from "./ManageCoEducationField";
import MdlAddExamClassSubject from "../dialog/MdlAddExamClassSubject";
import MdlArrangeSubjects from "../dialog/MdlArrangeSubjects";
import MdlAddExamParticipatingClass from "../dialog/MdlAddExamParticipatingClass";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const QUICK_LINKS = [
  {
    path: "ExamPaperSettings",
    label: "Paper Settings",
    icon: FaCog,
    tone: "blue",
  },
  {
    path: "ExamAddMarkSubjectWise",
    label: "Mark-Subject Wise",
    icon: FaBookOpen,
    tone: "teal",
  },
  {
    path: "ExamAddMarkStudentWise",
    label: "Mark-Student Wise",
    icon: FaUserEdit,
    tone: "cyan",
  },
  {
    path: "ExamMarkingSystem",
    label: "View Marks",
    icon: FaChartBar,
    tone: "indigo",
  },
  {
    path: "ExamDateSheet",
    label: "Date Sheet",
    icon: FaCalendarAlt,
    tone: "orange",
  },
  {
    path: "ExamGuideline",
    label: "Guideline",
    icon: FaListAlt,
    tone: "green",
  },
  {
    path: "ExamHallTicket",
    label: "Hall Ticket",
    icon: FaTicketAlt,
    tone: "sky",
  },
  {
    path: "ExamAdmitCard",
    label: "Admit Card",
    icon: FaIdCard,
    tone: "blue",
  },
  {
    path: "ExamResult",
    label: "Exam Result",
    icon: FaChartBar,
    tone: "indigo",
  },
  {
    path: "ManageCoEducationField",
    label: "Co-Education",
    icon: FaUserFriends,
    tone: "teal",
  },
  {
    path: "ManageSocialValueField",
    label: "Social Value",
    icon: FaHandshake,
    tone: "green",
  },
];

//Created By Cursor
function classLabel(item) {
  if (!item) return "Class";
  if (item.section_id === "No" || !item.section_name) return item.class_name || "Class";
  return `${item.class_name} - ${item.section_name}`;
}

//Created By Cursor
function subjectsForClass(allSubjects, classItem) {
  if (!classItem) return [];
  const sectionId = classItem.section_id || "No";
  let classSubjects = allSubjects.filter(
    (subject) =>
      subject.class_id === classItem.class_id &&
      (subject.section_id || "No") === sectionId
  );

  const orderIds = String(classItem.subject_ids || "")
    .split(",")
    .map((id) => id && String(id).trim())
    .filter(Boolean);

  if (orderIds.length > 0) {
    const indexById = new Map(orderIds.map((id, idx) => [id, idx]));
    classSubjects = [...classSubjects].sort((a, b) => {
      const ia = indexById.has(a.subject_id)
        ? indexById.get(a.subject_id)
        : Number.MAX_SAFE_INTEGER;
      const ib = indexById.has(b.subject_id)
        ? indexById.get(b.subject_id)
        : Number.MAX_SAFE_INTEGER;
      return ia - ib;
    });
  }

  return classSubjects;
}

const QuickLinks = memo(function QuickLinks({
  examId,
  sessionId,
  examName,
  examStatus,
}) {
  const base = `${examId}/${sessionId}/${encodeURIComponent(
    examName || ""
  )}/${encodeURIComponent(examStatus || "")}`;

  return (
    <div className="ed-quick__grid">
      {QUICK_LINKS.map((link) => {
        const Icon = link.icon;
        const to =
          link.path === "ExamMarkingSystem"
            ? `/AdminMain/${link.path}/${examId}/${sessionId}`
            : `/AdminMain/${link.path}/${base}`;
        return (
          <Link
            key={link.path}
            to={to}
            className={`ed-quick__link ed-quick__link--${link.tone}`}
            title={link.label}
          >
            <span className="ed-quick__icon" aria-hidden>
              <Icon />
            </span>
            <span className="ed-quick__label">{link.label}</span>
          </Link>
        );
      })}
    </div>
  );
});

const ExamDetails = () => {
  const { exam_id, session_id, exam_name, exam_status } = useParams();
  const loggedIn = localStorage.getItem("token") !== null;

  const [bootLoading, setBootLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [EXAM_ID] = useState(exam_id || "");
  const [SESSION_ID] = useState(session_id || "");
  const [EXAM_NAME] = useState(decodeURIComponent(exam_name || ""));
  const [EXAM_STATUS] = useState(
    decodeURIComponent(exam_status || "")
  );

  const [mClassList, setClassList] = useState([]);
  const [CLASS_SELECTED_POSITION, setClassSelectedPosition] = useState(0);
  const [SUJECT_SELECTED_POSITION, setSubjectSelectedPosition] = useState(0);
  const [mSubject, setSubject] = useState([]);
  const [mSubjectPaper, setSubjectPaper] = useState([]);
  const [allSubjects, setAllSubjects] = useState([]);

  const [showArrangeSubjectDialog, setShowArrangeSubjectDialog] =
    useState(false);
  const [showAddExamClassSubjectDialog, setShowAddExamClassSubjectDialog] =
    useState(false);
  const [showAddParticipatingClassDialog, setShowAddParticipatingClassDialog] =
    useState(false);
  const [selectedClassForAction, setSelectedClassForAction] = useState(null);

  //Created By Cursor
  const getExamParticipatingClass = useCallback(
    async ({ soft = false } = {}) => {
      if (!EXAM_ID || !SESSION_ID) {
        setClassList([]);
        setAllSubjects([]);
        setBootLoading(false);
        setRefreshing(false);
        return;
      }

      if (soft) setRefreshing(true);
      else setBootLoading(true);

      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode) {
        myToast.notify_warning("School code is missing.");
        setBootLoading(false);
        setRefreshing(false);
        return;
      }

      try {
        const response = await axios.get(
          `${API_BASE()}getClassWithSubjectsPartAndPaper.php`,
          {
            params: {
              school_code: schoolCode,
              exam_id: EXAM_ID,
              session_id: SESSION_ID,
            },
          }
        );

        if (response.data?.api_status === "OK") {
          const data = response.data.data || [];
          setClassList(data);
          setClassSelectedPosition(0);

          const allSubjectsFromClasses = [];
          data.forEach((classItem) => {
            if (Array.isArray(classItem.subject)) {
              classItem.subject.forEach((subject) => {
                allSubjectsFromClasses.push({
                  ...subject,
                  class_id: classItem.class_id,
                  class_name: classItem.class_name,
                  section_id: classItem.section_id,
                  section_name: classItem.section_name,
                });
              });
            }
          });
          setAllSubjects(allSubjectsFromClasses);
        } else {
          setClassList([]);
          setAllSubjects([]);
          myToast.notify_warning(
            response.data?.api_remark || "Could not load exam details."
          );
        }
      } catch (error) {
        console.error("Error fetching exam details:", error);
        setClassList([]);
        setAllSubjects([]);
        myToast.notify_warning("Network error loading exam details.");
      } finally {
        setBootLoading(false);
        setRefreshing(false);
      }
    },
    [EXAM_ID, SESSION_ID]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    getExamParticipatingClass();
  }, [getExamParticipatingClass]);

  //Created By Cursor
  const handleSelectClass = useCallback(
    (selectedPosition) => {
      if (!mClassList.length) {
        setSubject([]);
        setSubjectSelectedPosition(0);
        return;
      }
      const selectedSubjects = mClassList[selectedPosition]?.subject || [];
      setSubject(selectedSubjects);
      setSubjectSelectedPosition(0);
    },
    [mClassList]
  );

  useEffect(() => {
    if (CLASS_SELECTED_POSITION >= 0) {
      handleSelectClass(CLASS_SELECTED_POSITION);
    }
  }, [CLASS_SELECTED_POSITION, handleSelectClass]);

  useEffect(() => {
    const selectedSubjects = mSubject[SUJECT_SELECTED_POSITION];
    if (selectedSubjects) {
      const allSubjectPapers =
        mClassList[CLASS_SELECTED_POSITION]?.subject_paper || [];
      setSubjectPaper(
        allSubjectPapers.filter(
          (paper) => paper.subject_id === selectedSubjects.subject_id
        )
      );
    } else {
      setSubjectPaper([]);
    }
  }, [
    SUJECT_SELECTED_POSITION,
    mSubject,
    mClassList,
    CLASS_SELECTED_POSITION,
  ]);

  const selectedClass = mClassList[CLASS_SELECTED_POSITION] || null;
  const selectedSubject = mSubject[SUJECT_SELECTED_POSITION] || null;

  const kpi = useMemo(() => {
    const classCount = mClassList.length;
    const subjectCount = allSubjects.length;
    const paperCount = mClassList.reduce((sum, item) => {
      const papers = item.subject_paper;
      return sum + (Array.isArray(papers) ? papers.length : 0);
    }, 0);
    return { classCount, subjectCount, paperCount };
  }, [mClassList, allSubjects]);

  if (!loggedIn) return <Redirect to="/Login" />;

  const detailsView = bootLoading ? (
    <MyLoader />
  ) : (
    <div
      className="content payroll-page sm-page ed-page"
      style={{ background: COLORS.white, fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Exam Details</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaClipboardList />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">{EXAM_NAME || "Exam Details"}</h1>
              <p className="sm-hero__sub">
                Status: {EXAM_STATUS || "—"} · Classes, subjects & papers
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <span className="ed-status-pill" title="Exam status">
              {EXAM_STATUS || "—"}
            </span>
            <button
              type="button"
              onClick={() => getExamParticipatingClass({ soft: true })}
              disabled={refreshing}
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              title="Refresh"
              aria-label="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
            <Link to="/AdminMain/ExamList" className="ed-hero-link">
              Exam List
            </Link>
          </div>
        </header>

        <div className="ed-content">
          <div className="ed-kpi-row">
            <span className="ed-kpi">
              <FaUsers aria-hidden />
              {kpi.classCount} classes
            </span>
            <span className="ed-kpi">
              <FaBookOpen aria-hidden />
              {kpi.subjectCount} subjects
            </span>
            <span className="ed-kpi">
              <FaClipboardList aria-hidden />
              {kpi.paperCount} papers
            </span>
          </div>

          {/* Overview table */}
          <section className="ed-section-card" aria-label="Class overview">
            <div className="ed-section-card__header">
              <h2 className="ed-section-card__title">Class Overview</h2>
              <span className="ed-section-card__count">{mClassList.length}</span>
            </div>
            <div className="ed-section-card__body">
              {mClassList.length === 0 ? (
                <div className="ed-empty">
                  <p className="ed-empty__title">No participating classes</p>
                  <p className="ed-empty__hint">
                    Add a class to start configuring subjects and papers.
                  </p>
                </div>
              ) : (
                <div className="ed-table-wrap">
                  <table className="ed-table">
                    <thead>
                      <tr>
                        <th>Participating Class</th>
                        <th>Subjects</th>
                        <th>Papers</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mClassList.map((classItem, classIndex) => {
                        const sectionId = classItem.section_id || "No";
                        const classSubjects = subjectsForClass(
                          allSubjects,
                          classItem
                        );
                        const paperCount = Array.isArray(classItem.subject_paper)
                          ? classItem.subject_paper.length
                          : 0;

                        return (
                          <tr
                            key={`${classItem.class_id}-${sectionId}-${classIndex}`}
                          >
                            <td className="ed-table__class">
                              {classLabel(classItem)}
                            </td>
                            <td>
                              {classSubjects.length > 0 ? (
                                <div className="ed-chip-row">
                                  {classSubjects.map((subject, subjectIndex) => (
                                    <span
                                      key={`${subject.subject_id}-${subjectIndex}`}
                                      className="ed-chip"
                                    >
                                      {subject.subject_name}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="ed-muted">No subjects</span>
                              )}
                            </td>
                            <td>
                              <span className="ed-count-pill">{paperCount}</span>
                            </td>
                            <td>
                              <div className="ed-row-actions">
                                <button
                                  type="button"
                                  className="ed-btn"
                                  onClick={() => {
                                    setSelectedClassForAction(classItem);
                                    setShowAddExamClassSubjectDialog(true);
                                  }}
                                >
                                  <FaPlus aria-hidden /> Add Subject
                                </button>
                                <button
                                  type="button"
                                  className="ed-btn"
                                  onClick={() => {
                                    setSelectedClassForAction(classItem);
                                    setShowArrangeSubjectDialog(true);
                                  }}
                                >
                                  <FaSort aria-hidden /> Arrange
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>

          {/* Participating classes selector */}
          <section className="ed-section-card" aria-label="Participating classes">
            <div className="ed-section-card__header">
              <h2 className="ed-section-card__title">Participating Classes</h2>
              <button
                type="button"
                className="ed-header-btn"
                onClick={() => setShowAddParticipatingClassDialog(true)}
              >
                <FaPlus aria-hidden /> Add Class
              </button>
            </div>
            <div className="ed-section-card__body">
              {mClassList.length === 0 ? (
                <div className="ed-empty">
                  <p className="ed-empty__title">No classes yet</p>
                </div>
              ) : (
                <div className="ed-class-grid">
                  {mClassList.map((classItem, index) => (
                    <button
                      key={`${classItem.class_id}-${classItem.section_id}-${index}`}
                      type="button"
                      className={`ed-class-chip${
                        CLASS_SELECTED_POSITION === index ? " is-active" : ""
                      }`}
                      onClick={() => setClassSelectedPosition(index)}
                    >
                      {classItem.section_id === "No"
                        ? classItem.class_name
                        : `${classItem.class_name}: ${classItem.section_name}`}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Subjects of selected class */}
          <section className="ed-section-card" aria-label="Participating subjects">
            <div className="ed-section-card__header">
              <h2 className="ed-section-card__title">
                Subjects · {classLabel(selectedClass)}
              </h2>
              <span className="ed-section-card__count">{mSubject.length}</span>
            </div>
            <div className="ed-section-card__body">
              {mSubject.length === 0 ? (
                <div className="ed-empty">
                  <p className="ed-empty__title">No subjects for this class</p>
                  <p className="ed-empty__hint">
                    Use Add Subject from the overview table.
                  </p>
                </div>
              ) : (
                <div className="ed-subject-grid">
                  {mSubject.map((subject, index) => (
                    <button
                      key={`${subject.subject_id}-${index}`}
                      type="button"
                      className={`ed-subject-chip${
                        SUJECT_SELECTED_POSITION === index ? " is-active" : ""
                      }`}
                      onClick={() => setSubjectSelectedPosition(index)}
                    >
                      {subject.subject_name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Papers of selected subject */}
          <section className="ed-section-card" aria-label="Subject papers">
            <div className="ed-section-card__header">
              <h2 className="ed-section-card__title">
                Papers
                {selectedSubject?.subject_name
                  ? ` · ${selectedSubject.subject_name}`
                  : ""}
              </h2>
              <span className="ed-section-card__count">
                {mSubjectPaper.length}
              </span>
            </div>
            <div className="ed-section-card__body">
              {mSubjectPaper.length === 0 ? (
                <div className="ed-empty">
                  <p className="ed-empty__title">No papers for this subject</p>
                  <p className="ed-empty__hint">
                    Configure papers from Paper Settings.
                  </p>
                </div>
              ) : (
                <div className="ed-paper-grid">
                  {mSubjectPaper.map((paper, index) => (
                    <article
                      key={`${paper.exam_paper_id || paper.exam_paper_name}-${index}`}
                      className="ed-paper-card"
                    >
                      <h3 className="ed-paper-card__name">
                        {paper.exam_paper_name}
                      </h3>
                      <p className="ed-paper-card__meta">
                        {paper.is_mark_system === "1"
                          ? `Mark System · Max ${paper.max_mark}`
                          : `Grade System · Max ${paper.max_grade}`}
                      </p>
                    </article>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Quick links */}
          <section className="ed-section-card" aria-label="Quick links">
            <div className="ed-section-card__header">
              <h2 className="ed-section-card__title">Quick Links</h2>
            </div>
            <div className="ed-section-card__body">
              <QuickLinks
                examId={EXAM_ID}
                sessionId={SESSION_ID}
                examName={EXAM_NAME}
                examStatus={EXAM_STATUS}
              />
            </div>
          </section>
        </div>
      </div>

      <MdlAddExamClassSubject
        show={showAddExamClassSubjectDialog}
        onHide={() => setShowAddExamClassSubjectDialog(false)}
        examId={EXAM_ID}
        sessionId={SESSION_ID}
        classId={selectedClassForAction?.class_id}
        className={classLabel(selectedClassForAction)}
        sectionId={selectedClassForAction?.section_id || "No"}
        mClassList={mClassList}
        onSave={() => getExamParticipatingClass({ soft: true })}
      />

      <MdlArrangeSubjects
        show={showArrangeSubjectDialog}
        onHide={() => setShowArrangeSubjectDialog(false)}
        classItem={selectedClassForAction}
        allSubjects={allSubjects.filter(
          (s) => s.class_id === selectedClassForAction?.class_id
        )}
        examId={EXAM_ID}
        onSaved={(orderedIds) => {
          setAllSubjects((prev) => {
            const others = prev.filter(
              (s) => s.class_id !== selectedClassForAction?.class_id
            );
            const currentClassSubjects = prev.filter(
              (s) => s.class_id === selectedClassForAction?.class_id
            );
            const byId = new Map(
              currentClassSubjects.map((s) => [s.subject_id, s])
            );
            const reordered = orderedIds
              .map((id) => byId.get(id))
              .filter(Boolean);
            const remaining = currentClassSubjects.filter(
              (s) => !orderedIds.includes(s.subject_id)
            );
            return [...others, ...reordered, ...remaining];
          });
          if (
            mClassList[CLASS_SELECTED_POSITION]?.class_id ===
            selectedClassForAction?.class_id
          ) {
            const byId2 = new Map(
              allSubjects
                .filter((s) => s.class_id === selectedClassForAction?.class_id)
                .map((s) => [s.subject_id, s])
            );
            setSubject(orderedIds.map((id) => byId2.get(id)).filter(Boolean));
          }
          setShowArrangeSubjectDialog(false);
          getExamParticipatingClass({ soft: true });
        }}
      />

      <MdlAddExamParticipatingClass
        show={showAddParticipatingClassDialog}
        onHide={() => setShowAddParticipatingClassDialog(false)}
        examId={EXAM_ID}
        sessionId={SESSION_ID}
        onSaved={() => {
          setShowAddParticipatingClassDialog(false);
          getExamParticipatingClass({ soft: true });
        }}
      />
    </div>
  );

  return (
    <Router>
      <Switch>
        <Route
          path="/AdminMain/ExamDetails/:exam_id/:session_id/:exam_name/:exam_status"
          exact
        >
          {detailsView}
        </Route>
        <Route
          path="/AdminMain/ExamDateSheet/:exam_id/:session_id/:exam_name/:exam_status"
          component={ExamDateSheet}
        />
        <Route
          path="/AdminMain/ExamGuideline/:exam_id/:session_id/:exam_name/:exam_status"
          component={ExamGuideline}
        />
        <Route
          path="/AdminMain/ExamPaperSettings/:exam_id/:session_id/:exam_name/:exam_status"
          component={ExamPaperSettings}
        />
        <Route
          path="/AdminMain/ExamHallTicket/:exam_id/:session_id/:exam_name/:exam_status"
          component={ExamHallTicket}
        />
        <Route
          path="/AdminMain/ExamAdmitCard/:exam_id/:session_id/:exam_name/:exam_status"
          component={ExamAdmitCard}
        />
        <Route
          path="/AdminMain/ExamMarkingSystem/:exam_id/:session_id"
          component={ExamMarkingSystem}
        />
        <Route
          path="/AdminMain/ExamAddMarkStudentWise/:exam_id/:session_id/:exam_name/:exam_status"
          component={ExamAddMarkStudentWise}
        />
        <Route
          path="/AdminMain/ExamAddMarkSubjectWise/:exam_id/:session_id/:exam_name/:exam_status"
          component={ExamAddMarkSubjectWise}
        />
        <Route
          path="/AdminMain/ManageSocialValueField/:exam_id/:session_id/:exam_name/:exam_status"
          component={ManageSocialValueField}
        />
        <Route
          path="/AdminMain/ManageCoEducationField/:exam_id/:session_id/:exam_name/:exam_status"
          component={ManageCoEducationField}
        />
      </Switch>
    </Router>
  );
};

export default ExamDetails;
